import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { styled, Stack, palette, useUtility, useIsMobile, Box, Text, Divider } from '@hub-la/design-system';
import { DiscussionProvider, Discussion } from '@hub-la/fe-discussion';
import { useRoleplay, useGetRoleplayReference } from '@hub-la/fe-roleplay';
import { LinearProgress } from '@mui/material';
import Parser from 'html-react-parser';
import moment from 'moment';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../domain/dtos/alert-type.js';
import '../../../domain/dtos/error-type.js';
import '../../../domain/dtos/main-content-type.js';
import '../../../domain/dtos/post-event-type.js';
import { PostStatus } from '../../../domain/dtos/post-status.js';
import '../../../domain/dtos/query-key.js';
import '../../../domain/dtos/remove-dialog-action.js';
import { IsPostContentHidden } from '../../../usecases/is-post-content-hidden.js';
import useCurrentUser from '../../hooks/use-current-user.js';
import { useGetMembersArea } from '../../hooks/use-get-members-area.js';
import { DateRange } from '../date-range.js';
import { EventCard } from '../event-card/event-card.js';
import { EventLiveTag } from '../event-live-tag/event-live-tag.js';
import { PostAttachmentView } from '../post-attachment-list/post-attachment-view.js';
import { PostDetailCover } from '../post-detail-cover/post-detail-cover.js';
import { Sidebar } from '../sidebar/sidebar.js';
import { SidebarHeader } from '../sidebar/sidebar-header.js';
import { TimeToReleaseCard } from '../time-to-release-card/time-to-release-card.js';

let _ = t => t,
  _t;
const Wrapper = styled(Stack)(_t || (_t = _`
  img {
    max-width: 100%;
  }
  * {
    background-color: transparent !important;
    color: ${0} !important;
    font-family: 'Satoshi', sans-serif !important;
  }
`), palette('onBackground'));
const PostContent = props => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
  const {
    post,
    loading,
    timeToRelease,
    canAccess
  } = props;
  const firebaseCurrentUser = useCurrentUser();
  // prevent undefined value feature flag
  const isBeta = (_a = props === null || props === void 0 ? void 0 : props.isBeta) !== null && _a !== void 0 ? _a : false; // todo: remove this after rollout sections v2 endpoint.
  const {
    isRoleplay
  } = useRoleplay();
  const {
    data: roleplayUserData
  } = useGetRoleplayReference({
    enabled: isRoleplay
  });
  const currentUser = isRoleplay ? roleplayUserData : firebaseCurrentUser;
  const currentUserId = isRoleplay ? roleplayUserData === null || roleplayUserData === void 0 ? void 0 : roleplayUserData.userId : firebaseCurrentUser === null || firebaseCurrentUser === void 0 ? void 0 : firebaseCurrentUser.id;
  // minutes to show the event button to buyers enter the event
  const minutesBeforeEventStart = 10;
  const initialStarted = post.event ? moment(new Date()).isBetween(moment(post.event.startTime).subtract(minutesBeforeEventStart, 'minutes'), moment(post.event.endTime)) : false;
  const [started, setStarted] = useState(initialStarted);
  const {
    t
  } = useTranslation();
  const {
    spacing
  } = useUtility();
  const isMobile = useIsMobile();
  const today = moment();
  const dateDiff = today.diff((_b = post === null || post === void 0 ? void 0 : post.updatedAt) !== null && _b !== void 0 ? _b : post === null || post === void 0 ? void 0 : post.createdAt, 'days');
  const isDraft = (post === null || post === void 0 ? void 0 : post.status) === PostStatus.DRAFT;
  const {
    data
  } = useGetMembersArea({
    productId: post.product.id,
    enabled: canAccess
  });
  const showComments = canAccess && (data === null || data === void 0 ? void 0 : data.postComment);
  const isPostContentHidden = new IsPostContentHidden().execute(post);
  const releaseAt = new Date((_c = post.releaseAt) !== null && _c !== void 0 ? _c : 0);
  useEffect(() => {
    setStarted(initialStarted);
  }, [post]);
  const canRenderEventCard = post.roleType !== 'not-member';
  return jsxs(Stack, Object.assign({
    id: "post-detail",
    direction: "column",
    spacing: 4,
    px: [4, 10],
    pb: 30,
    bgcolor: "rgba(22, 31, 0, 0.05)"
  }, {
    children: [jsx(Box, Object.assign({
      style: {
        width: '100%',
        height: '1px'
      }
    }, {
      children: loading && jsx(LinearProgress, {
        color: "inherit",
        style: {
          height: '1px'
        }
      })
    })), jsxs(Stack, Object.assign({
      direction: ['column', 'column', 'row'],
      width: "100%"
    }, {
      children: [jsxs(Stack, Object.assign({
        direction: "column",
        spacing: 2,
        position: "relative",
        /** 395 is the size of the sidebar, spacing(4) is it's margin */
        width: ['100%', '100%', `calc(100% - 395px - ${spacing(4)})`]
      }, {
        children: [jsx(PostDetailCover, {
          cover: post.cover,
          customCover: (_d = post.customCover) === null || _d === void 0 ? void 0 : _d.url,
          sectionId: post.sectionId,
          postId: post.id,
          hideContent: isPostContentHidden,
          hideText: canAccess ? timeToRelease : t('postDetail.paywall.onlyMembers'),
          isLive: started
        }), started && jsx(EventLiveTag, {}), isMobile && jsx(Stack, {
          children: jsx(SidebarHeader, {
            productId: post.product.id,
            isBeta: isBeta
          })
        }), jsx(Text, Object.assign({
          color: "onBackground",
          variant: "h3",
          mb: 4,
          pt: 4
        }, {
          children: post.title
        })), jsx(Text, Object.assign({
          color: "onSurfaceVariant",
          mb: 2,
          variant: "body1"
        }, {
          children: post.subtitle
        })), post.event && jsxs(Fragment, {
          children: [jsx(DateRange, {
            startTime: new Date((_e = post.event) === null || _e === void 0 ? void 0 : _e.startTime),
            endTime: new Date((_f = post.event) === null || _f === void 0 ? void 0 : _f.endTime)
          }), canRenderEventCard && jsx(EventCard, {
            startTime: new Date((_g = post.event) === null || _g === void 0 ? void 0 : _g.startTime),
            endTime: new Date((_h = post.event) === null || _h === void 0 ? void 0 : _h.endTime),
            type: (_j = post.event) === null || _j === void 0 ? void 0 : _j.urlType,
            liveId: post.id,
            started: started,
            onComplete: () => setStarted(true)
          })]
        }), canAccess && releaseAt > new Date() && jsx(TimeToReleaseCard, {
          timeToRelease: releaseAt
        }), jsx(Divider, {
          my: 8
        }), jsx(Wrapper, Object.assign({
          pt: 2
        }, {
          children: Parser((_k = post.content) !== null && _k !== void 0 ? _k : '')
        })), post.attachments && post.attachments.length > 0 && jsxs(Stack, Object.assign({
          pt: 3
        }, {
          children: [jsx(Text, Object.assign({
            color: "onBackground",
            variant: "h4",
            mb: 4,
            fontWeight: "medium"
          }, {
            children: t('postDetail.assets.title')
          })), jsx(PostAttachmentView, {
            assets: post.attachments
          })]
        })), jsxs(Text, Object.assign({
          color: "onBackground",
          pt: 4,
          variant: "body2",
          pb: 8
        }, {
          children: [isDraft ? 'Atualizado' : 'Publicado', dateDiff > 7 ? ' em' : '', ' ', moment((_l = post.updatedAt) !== null && _l !== void 0 ? _l : post.createdAt).locale('pt-br').format('DD MMM. YYYY')]
        })), showComments && jsx(DiscussionProvider, {
          children: jsx(Discussion, {
            discussionOwnerId: (_o = (_m = post === null || post === void 0 ? void 0 : post.product) === null || _m === void 0 ? void 0 : _m.owner) === null || _o === void 0 ? void 0 : _o.id,
            type: "post",
            metadata: {
              postId: post.id
            },
            currentUser: {
              id: currentUserId,
              name: currentUser === null || currentUser === void 0 ? void 0 : currentUser.name,
              picture: currentUser === null || currentUser === void 0 ? void 0 : currentUser.picture
            }
          })
        })]
      })), jsx(Stack, {
        children: jsx(Sidebar, {
          productId: post.product.id,
          post: {
            id: post.id,
            cover: post.cover,
            title: post.title
          },
          isBeta: isBeta
        })
      })]
    }))]
  }));
};

export { PostContent };
